<template>
  <OverlayLoader :show="loading" />
  <nav class="shadow-none navbar navbar-main navbar-expand-lg border-radius-xl" v-bind="$attrs" id="navbarBlur"
    data-scroll="true" :class="stIsAbsolute ? 'mt-4' : 'mt-0'">
    <div class="px-3 py-1 container-fluid w-100">
      <div class="w-100 d-flex align-items-center justify-content-between p-0 m-0 navbar-content-wrapper">
        <!-- Left section: Breadcrumbs (desktop) / Menu + Dropdown (mobile) -->
        <div class="d-flex align-items-center gap-2 flex-nowrap navbar-left-section">
          <!-- Mobile menu button -->
          <button
            class="btn btn-link p-2 d-md-none m-0 mobile-menu-btn"
            type="button"
            @click="toggleMobileSidenav"
            aria-label="Toggle menu">
            <i class="material-icons">menu</i>
          </button>
          
          <div class="d-none d-md-block">
            <Breadcrumbs :currentPage="currentRouteName" :color="color" />
          </div>
          <div class="position-relative navbar-dropdown-section" style="min-width: 200px;">
            <Dropdown :key="locationKey" :dropdownList="dropdownList" @selectedValue="updateSelectedLocation" />
          </div>
          <!-- Subtle "get the mobile app" icon: shown only to non-admin users on Android devices -->
          <MobileAppInstallLink />
        </div>

        <!-- Quote Section (Desktop only) -->
        <div v-if="hasQuote" class="p-0 grow d-none d-md-block" @click="openModal">
          <Qoute :qoute="quote" />
        </div>

        <!-- Impersonation indicator -->
        <div v-if="isImpersonating" class="d-none d-md-flex align-items-center gap-1 px-2 py-1 rounded me-2" 
          style="background-color: #fff3cd; border: 1px solid #ffecb5; white-space: nowrap;">
          <i class="material-icons" style="font-size: 16px; color: #997404;">visibility</i>
          <span class="text-xs fw-semibold" style="color: #664d03;">Viewing as admin</span>
        </div>

        <!-- User dropdown -->
        <div class="justify-content-end navbar-user-section" id="navbar">
          <ul class="navbar-nav justify-content-center">
            <li v-if="route.path !== '/'" class="nav-item d-flex align-items-center me-2">
              <MaterialButton class="text-white d-flex align-items-center p-2.5 btn-warning" @click="goToDashboard"
                title="Go to Dashboard" style="text-decoration: none;">
                <i class="material-icons">home</i>
              </MaterialButton>
            </li>
            <li class="nav-item d-flex align-items-center">
              <div class="dropdown">
                <button
                  class="btn dropdown-toggle user-dropdown-btn d-flex align-items-center gap-2 px-3 py-2 text-white bg-dark"
                  type="button" id="userDropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                  <i class="material-icons">account_circle</i>
                  <span class="fw-semibold">{{ userFullName || 'User' }}</span>
                  <i class="material-icons fs-6 opacity-75">expand_more</i>
                </button>

                <ul class="dropdown-menu dropdown-menu-end shadow-sm border-0 rounded-3 mt-2 p-2 user-dropdown-menu"
                  aria-labelledby="userDropdownMenuButton">
                  <li class="dropdown-header text-center">
                    <i class="material-icons text-info mb-1" style="font-size: 32px;">account_circle</i>
                    <p class="mb-0 fw-semibold">{{ userFullName || 'User' }}</p>
                  </li>
                  <hr class="dropdown-divider" />

                  <li>
                    <p class="dropdown-item d-flex align-items-center gap-2 mb-0 text-muted small">
                      <i class="material-icons fs-6 text-secondary">email</i>
                      {{ userEmail || 'No email available' }}
                    </p>
                  </li>

                  <hr class="dropdown-divider" />

                  <li>
                    <p class="dropdown-item d-flex align-items-center gap-2 mb-0 text-muted small" :aria-label="`Dashboard version ${APP_VERSION}`">
                      <i class="material-icons fs-6 text-secondary">info_outline</i>
                      Dashboard v{{ APP_VERSION }}
                    </p>
                  </li>

                  <hr class="dropdown-divider" />

                  <li>
                    <p class="dropdown-item d-flex align-items-center gap-2 mb-0 text-danger fw-semibold logout-item"
                      @click="logoutUser">
                      <i class="material-icons fs-6">logout</i>
                      Logout
                    </p>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </div>

    </div>
  </nav>

  <Modal v-if="isModalOpen && hasQuote" :modalHeading="modalHeading" :modalContent="modalContent" @close="closeModal">
    <template #header>
      <h5 class="d-flex align-items-center gap-2 mb-0" style="color: var(--bs-info);">
        <i class="material-icons text-info">format_quote</i>
        Inspirational Quote
      </h5>
    </template>
  </Modal>

</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs.vue";
import Dropdown from "../common/Dropdown.vue";
import Qoute from "../Qoutes/Qoute.vue";
import Modal from "@/components/Modal/Modal.vue";
import { getLocalStorage } from "@/utils/LocalStorageHandler.js";
import OverlayLoader from "../Loader/OverlayLoader.vue";
import MaterialButton from "../common/MaterialButton.vue";
import MobileAppInstallLink from "./MobileAppInstallLink.vue";
import { APP_VERSION } from "@/config";

const loading = ref(false);

// Props
const props = defineProps({
  minNav: Boolean,
  color: String,
  dropdownList: Array,
  quote: Object,
});

const router = useRouter()

// Emits
const emit = defineEmits(["selectedValue"]);

// Vuex
const store = useStore();
const stIsAbsolute = computed(() => store.getters.stIsAbsolute);
const stLogoutResponse = computed(() => store.getters.stLogoutResponse);
const stSessionData = computed(() => store.getters.stSessionData);

// Router
const route = useRoute();
const currentRouteName = computed(() => route.name);

// Local state
const isModalOpen = ref(false);
const modalContent = ref("");

// ✅ Get session data from localStorage
const sessionData = ref({});
onMounted(() => {
  sessionData.value = getLocalStorage("sessionData") || {};
  modalContent.value = `<div style="text-align: center;">"${props.quote?.code?.text}" <br> - ${props.quote?.code?.reference}</div>`;
});

// ✅ Computed full name
const userFullName = computed(() => {
  const first = sessionData?.value.firstName || "";
  const last = sessionData?.value.lastName || "";
  return `${first} ${last}`.trim();
});

// Detect if superadmin is viewing/impersonating a location they don't normally belong to
const isImpersonating = computed(() => {
  const session = stSessionData.value || sessionData.value || {};
  if (!session.isSuperAdmin) return false;
  // Check if any location in the dropdown is marked as impersonated
  const locations = props.dropdownList || [];
  const currentLocationId = String(session.locationid || '');
  if (!currentLocationId) return false;
  for (const org of locations) {
    for (const loc of org.locations || []) {
      if (String(loc.location_id) === currentLocationId && loc.is_impersonated) {
        return true;
      }
    }
  }
  return false;
});

const userEmail = computed(() => {
  return sessionData?.value.emailAddress || "";
});

// Only show the quote label/popup when the API actually returned a quote.
// When inspirations are disabled, the API responds with code: [] (empty
// array), so we skip rendering the activator button and modal entirely.
const hasQuote = computed(() => {
  const code = props.quote && props.quote.code;
  if (!code) return false;
  if (Array.isArray(code)) return false;
  return Boolean(code.text);
});

// Computed location key to force Dropdown re-mount when location changes
const locationKey = computed(() => {
  // Get locationId from store sessionData or localStorage
  const locationId = stSessionData.value?.locationid || sessionData.value?.locationid || getLocalStorage("sessionData")?.locationid || '';
  return `location-${locationId}`;
});

// Watch for quote updates
watch(
  () => props.quote,
  (newQuote) => {
    modalContent.value = `<div style="text-align: center;">"${newQuote?.code?.text}" <br> - ${newQuote?.code?.reference}</div>`;
  },
  { deep: true, immediate: true }
);

// Watch for session data changes to update local sessionData and trigger Dropdown re-mount
watch(
  () => stSessionData.value,
  (newSessionData) => {
    if (newSessionData) {
      sessionData.value = newSessionData;
    }
  },
  { deep: true, immediate: true }
);

// Watch for location and dropdownList changes to ensure Dropdown updates
watch(
  [() => stSessionData.value?.locationid || getLocalStorage("sessionData")?.locationid, () => props.dropdownList],
  ([newLocationId, newDropdownList]) => {
    // When both location and dropdownList are available, the key will update
    // which forces Dropdown to re-mount and read the correct location
    if (newLocationId && newDropdownList && newDropdownList.length > 0) {
      // The locationKey computed will automatically update, forcing Dropdown re-mount
      // This ensures the selected location is displayed
    }
  },
  { immediate: true, deep: true }
);

// Methods
const openModal = () => {
  if (!hasQuote.value) return;
  isModalOpen.value = true;
};
const closeModal = () => {
  isModalOpen.value = false;
};
const updateSelectedLocation = (location) => {
  // Check if current route has location parameter and update it
  const routeParams = route.params;
  const locationParamKeys = ['locationID', 'locationid', 'locationId'];

  // Find if route has any location parameter
  const locationParamKey = locationParamKeys.find(key => routeParams[key]);

  if (locationParamKey && routeParams[locationParamKey] !== location) {
    // Update route with new location ID
    const newParams = { ...routeParams };
    newParams[locationParamKey] = location;

    router.push({
      name: route.name,
      params: newParams,
      query: route.query
    });
  }

  emit("selectedValue", location);
};
const goToDashboard = () => {
  router.push("/");
};
const toggleMobileSidenav = () => {
  // Toggle sidenav visibility on mobile
  store.commit('toggleSidenavCollapse');
};
const logoutUser = async () => {
  loading.value = true;
  await store.dispatch("logout");
  store.commit("SET_SIDEBAR_ROUTES", null);
  window.$toast?.success(stLogoutResponse.value.message, {
    position: "top-right",
  });
  localStorage.clear();
  loading.value = false;
  router.push({ name: "SignIn" })
};
</script>


<style scoped>
.dropdown-menu {
  min-width: 150px;
  margin-left: -20px;
}

.dropdown-toggle {
  border: 0px;
  outline: 0px;
  margin: 0px;
  display: flex;
  align-items: center;
}

.dropdown-item {
  cursor: pointer;
}

.dropdown-toggle::after {
  display: none !important;
}

/* Mobile responsive styles */
@media (max-width: 767.98px) {
  .navbar {
    flex-wrap: wrap;
    z-index: 1030 !important; /* Lower than sidenav (1050) and backdrop (1040) */
  }
  
  .container-fluid {
    flex-wrap: wrap;
    overflow: visible;
  }
  
  /* Stack vertically on mobile */
  .navbar-content-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    width: 100%;
  }
  
  /* Menu button and dropdown side by side */
  .navbar-left-section {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
  }
  
  .navbar-dropdown-section {
    flex: 1;
    min-width: 0;
  }
  
  .navbar-user-section {
    width: 100%;
    justify-content: flex-start !important;
  }
  
  /* Ensure dropdown doesn't expand navbar */
  .position-relative {
    position: relative !important;
  }
  
  /* User dropdown menu should be absolute */
  .user-dropdown-menu {
    position: absolute !important;
    z-index: 1050;
  }
  
  /* Mobile menu button styling */
  .mobile-menu-btn {
    border: none;
    outline: none;
    text-decoration: none;
    color: #333 !important;
    background-color: rgba(255, 255, 255, 0.9) !important;
    border-radius: 4px;
    min-width: 40px;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  
  .mobile-menu-btn:hover {
    background-color: rgba(255, 255, 255, 1) !important;
    opacity: 0.9;
  }
  
  .mobile-menu-btn:focus {
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    outline: none;
  }
  
  .mobile-menu-btn .material-icons {
    color: #333 !important;
    font-size: 24px;
  }
}
</style>